import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Notifications as NotificationIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  SupportAgent as SupportIcon,
  Info as InfoIcon,
  Router as RouterIcon,
  Sync as SyncIcon,
  People as PeopleIcon,
  CardGiftcard as GiftIcon,
  Star as StarIcon
} from '@mui/icons-material';
import MySaaSPlanModal from './saas/MySaaSPlanModal';
import {
  IconButton,
  Button,
  Tooltip,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Avatar,
  Popover,
  List,
  ListItem,
  Paper,
  InputBase,
  alpha,
  Chip
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';

function Navbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const [companySettings, setCompanySettings] = useState({ company_name: 'YinoWISP', company_logo: '' });

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch('/api/public/company-settings');
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            company_name: data.company_name || 'YinoWISP',
            company_logo: data.company_logo || ''
          });
        }
      } catch (error) {
        console.error('Error fetching company settings for Navbar:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const notificationsOpen = Boolean(notificationAnchorEl);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Estado para Modal de Suscripción SaaS
  const [saasModalOpen, setSaasModalOpen] = useState(false);

  // Estados para salud de routers
  const [routersHealth, setRoutersHealth] = useState([]);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [routerAnchorEl, setRouterAnchorEl] = useState(null);
  const routerMenuOpen = Boolean(routerAnchorEl);

  const fetchHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch('/api/routers/health-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setRoutersHealth(data);
        }
      }
    } catch (error) {
      console.error('Error al consultar estado de MikroTiks en Navbar:', error);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 45000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const handleRouterMenuOpen = (event) => {
    setRouterAnchorEl(event.currentTarget);
  };

  const handleRouterMenuClose = () => {
    setRouterAnchorEl(null);
  };

  // Obtener notificaciones del servidor
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);

        // Calcular número de notificaciones no leídas
        const unread = data.filter(notification => !notification.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Configurar intervalo para actualizar notificaciones cada 60 segundos como fallback resiliente
    const interval = setInterval(fetchNotifications, 60000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Conexión Socket.IO para notificaciones de administrador en tiempo real
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;

    console.log('🔌 Inicializando socket de administración en Navbar...');
    let socketInstance;

    try {
      socketInstance = io(process.env.REACT_APP_SOCKET_URL || '', {
        auth: {
          token,
          type: 'admin'
        },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      socketInstance.on('connect', () => {
        console.log('⚡ Socket de administración conectado (ID:', socketInstance.id, ')');
      });

      socketInstance.on('connect_error', (error) => {
        console.error('❌ Error de conexión de socket admin:', error.message);
      });

      // Escuchar notificaciones de administración en tiempo real
      socketInstance.on('admin_notification', (newNotification) => {
        console.log('🔔 Nueva notificación en tiempo real recibida:', newNotification);
        
        setNotifications(prev => {
          // Prevenir notificaciones duplicadas
          if (prev.some(n => n.id === newNotification.id)) return prev;
          return [newNotification, ...prev];
        });
        
        setUnreadCount(prev => prev + 1);
      });

      socketInstance.on('disconnect', (reason) => {
        console.log('🔌 Socket de administración desconectado:', reason);
      });

    } catch (err) {
      console.error('🚨 Error al crear socket de administrador:', err);
    }

    return () => {
      if (socketInstance) {
        console.log('🔌 Cerrando conexión de socket de administración en Navbar');
        socketInstance.disconnect();
      }
    };
  }, [user]);

  // Función para marcar notificación como leída
  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Actualizar el estado local
        setNotifications(prevNotifications =>
          prevNotifications.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );

        // Recalcular contador de no leídas
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Función interactiva para marcar como leída y redirigir
  const handleNotificationClick = async (notification) => {
    // Marcar como leída si está pendiente
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Cerrar menú desplegable
    handleNotificationMenuClose();

    // Determinar la ruta correspondiente
    if (notification.reference_type === 'promotion' || notification.type === 'promotion') {
      navigate('/finance/promotions');
    } else if (notification.reference_type === 'ticket' || notification.type?.startsWith('support')) {
      navigate('/support/tickets');
    } else if (notification.reference_type === 'payment' || notification.type?.startsWith('payment')) {
      navigate('/finance/invoices?tab=pending');
    } else if (notification.reference_type === 'invoice' || notification.type?.startsWith('invoice')) {
      if (notification.reference_id) {
        navigate(`/finance/invoices/${notification.reference_id}`);
      } else {
        navigate('/finance/invoices');
      }
    } else if (notification.client_id) {
      navigate(`/clients/edit/${notification.client_id}`);
    } else {
      navigate('/admin');
    }
  };

  // Función para marcar todas las notificaciones como leídas
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/notifications/read-all', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Actualizar estado local
        setNotifications(prevNotifications =>
          prevNotifications.map(notification => ({ ...notification, read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Obtener el icono según el tipo de notificación
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'payment':
        return <PaymentIcon fontSize="small" />;
      case 'support':
        return <SupportIcon fontSize="small" />;
      case 'promotion':
        return <GiftIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  // Obtener el color según el tipo de notificación
  const getNotificationColor = (type) => {
    switch (type) {
      case 'payment':
        return 'success.light';
      case 'support':
        return 'info.light';
      case 'promotion':
        return 'primary.light';
      default:
        return 'warning.light';
    }
  };

  // Manejadores de eventos
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchInputRef.current.value.trim()) {
      navigate(`/clients/list?search=${encodeURIComponent(searchInputRef.current.value.trim())}`);
      searchInputRef.current.value = '';
    }
  };

  return (
    <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Sección izquierda */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {companySettings.company_logo && (
            <img 
              src={companySettings.company_logo} 
              alt="Logo" 
              style={{ height: 28, marginRight: 10, objectFit: 'contain' }} 
            />
          )}
          <Typography
            variant="h6"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {companySettings.company_name}
          </Typography>
        </Box>

        {/* Sección derecha */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Estado de Routers */}
          <Tooltip title="Estado de Nodos MikroTik">
            <IconButton
              color="inherit"
              onClick={handleRouterMenuOpen}
              aria-controls={routerMenuOpen ? 'router-menu' : undefined}
              aria-haspopup="true"
            >
              <Badge 
                variant="dot" 
                color={
                  routersHealth.length === 0 ? "default" :
                  routersHealth.every(r => r.status === 'online') ? "success" : "error"
                }
              >
                <RouterIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Botón de ayuda */}
          <Tooltip title="Ayuda">
            <IconButton color="inherit">
              <HelpIcon />
            </IconButton>
          </Tooltip>

          {/* Notificaciones */}
          <Tooltip title="Notificaciones">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              aria-controls={notificationsOpen ? 'notification-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={notificationsOpen ? 'true' : undefined}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Menú de perfil */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleUserMenuOpen}>
            <Avatar
              src={user?.avatar}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                marginRight: 1,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.username || 'Usuario'}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1 }}>
                {user?.role === 'admin' ? 'Administrador' : user?.role || 'Usuario'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Menú de notificaciones */}
        <Popover
          id="notification-menu"
          anchorEl={notificationAnchorEl}
          open={notificationsOpen}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: { width: 360, maxHeight: 450, overflow: 'auto' }
          }}
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
            <Typography variant="h6">Notificaciones</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={markAllAsRead}>
                Marcar todas como leídas
              </Button>
            )}
          </Box>

          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="textSecondary">No hay notificaciones</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map(notification => (
                <ListItem
                  key={notification.id}
                  alignItems="flex-start"
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    bgcolor: notification.read ? 'transparent' : alpha('#2196F3', 0.05),
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      bgcolor: alpha('#2196F3', 0.08),
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                    }
                  }}
                  secondaryAction={
                    !notification.read && (
                      <IconButton 
                        edge="end" 
                        aria-label="mark as read" 
                        size="small" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          markAsRead(notification.id); 
                        }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemIcon sx={{ mt: 1 }}>
                    <Avatar sx={{ bgcolor: getNotificationColor(notification.type), width: 32, height: 32 }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {notification.title}
                        {!notification.read && (
                          <Chip
                            label="Nuevo"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1, height: 20, fontSize: '0.625rem' }}
                          />
                        )}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textPrimary" component="span">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" component="div">
                          {new Date(notification.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Popover>

        {/* Menú de routers */}
        <Popover
          id="router-menu"
          anchorEl={routerAnchorEl}
          open={routerMenuOpen}
          onClose={handleRouterMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: { width: 280, maxHeight: 350, p: 2 }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #eee', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <RouterIcon fontSize="small" color="primary" />
              Nodos MikroTik
            </Typography>
            <IconButton 
              size="small" 
              onClick={fetchHealth} 
              disabled={loadingHealth}
              sx={{ animation: loadingHealth ? 'spin 1s linear infinite' : 'none' }}
            >
              <SyncIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {routersHealth.length === 0 ? (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                Cargando routers...
              </Typography>
            ) : (
              routersHealth.map(r => (
                <Box 
                  key={r.id} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    p: 1, 
                    borderRadius: 1, 
                    '&:hover': { bgcolor: '#f5f5f5' } 
                  }}
                >
                  <Box sx={{ minWidth: 0, mr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, truncate: true }}>
                      {r.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontMono: true }}>
                      {r.ip}:{r.port}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {r.clientCount > 0 && (
                      <Chip 
                        size="small" 
                        icon={<PeopleIcon style={{ fontSize: 12 }} />} 
                        label={r.clientCount} 
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.5 } }}
                      />
                    )}
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: r.status === 'online' ? '#2e7d32' : '#d32f2f',
                        boxShadow: r.status === 'online' ? '0 0 6px #2e7d32' : '0 0 6px #d32f2f'
                      }} 
                    />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Popover>

        {/* Menú de usuario */}
        <Menu
          id="user-menu"
          anchorEl={userMenuAnchorEl}
          open={userMenuOpen}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.username || 'Usuario'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user?.email || ''}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/system/profile'); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Mi Perfil" />
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/system/settings'); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Configuración" />
          </MenuItem>
          <MenuItem onClick={() => { handleUserMenuClose(); setSaasModalOpen(true); }}>
            <ListItemIcon>
              <StarIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="💳 Mi Suscripción SaaS" />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Cerrar Sesión" />
          </MenuItem>
        </Menu>

        <MySaaSPlanModal open={saasModalOpen} onClose={() => setSaasModalOpen(false)} />
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;